import type { ChartDocumentV1, Difficulty } from './schema/ChartDocumentV1.js';
import type { SpawnPositionMode } from './schema/SpawnPositionMode.js';
import type { ColorV1, Vector2V1, WaveEventV1 } from './schema/WaveEventV1.js';

/**
 * Turns a validated `ChartDocumentV1` into the runtime-facing `CompiledChart`.
 *
 * This is a **transform, not an interpretation** (requirement 12). Specifically:
 *
 * - No value is altered, clamped, rounded or substituted. Every event field is
 *   copied across verbatim.
 * - Events keep their **source order**. Ascending-time traversal is provided by a
 *   separate `order` index, so the runtime never has to mutate the chart and the
 *   original ordering stays observable.
 * - Absent `detectedBeatTimes` is reported through `meta.hasDetectedBeatTimes`
 *   rather than being back-filled. Synthesising a beat grid is the audio layer's
 *   documented fallback, and doing it here would invent data.
 * - The only values that differ from the source document are the four runtime
 *   hints (`poolCapacity` and friends), which are clamped exactly as Unity's
 *   `ChartDocument.ApplyTo` clamps them. Which ones were clamped is reported in
 *   `meta.clampedHints`, and the unclamped values remain on the source document.
 *
 * Layout is struct-of-arrays over typed arrays: this is what the simulation wants
 * for allocation-free iteration, what a Worker wants for transfer, and what the
 * renderer will later want for instance buffers.
 */

/** Struct-of-arrays storage for a chart's wave events, in source order. */
export interface CompiledWaveEvents {
  readonly count: number;
  /**
   * Seconds. Held at double precision: the producers store `float`, so this is
   * lossless with respect to the wire format and leaves beat alignment exact.
   */
  readonly time: Float64Array;
  readonly beat: Float64Array;
  readonly initialRadius: Float32Array;
  readonly intensity: Float32Array;
  readonly spawnPositionMode: Uint8Array;
  readonly originX: Float32Array;
  readonly originY: Float32Array;
  readonly fixedWorldPositionX: Float32Array;
  readonly fixedWorldPositionY: Float32Array;
  readonly normalizedPositionX: Float32Array;
  readonly normalizedPositionY: Float32Array;
  readonly edgePadding: Float32Array;
  readonly minimumDistanceFromPlayer: Float32Array;
  readonly randomSeedOffset: Int32Array;
  readonly warningLeadTime: Float32Array;
  readonly speed: Float32Array;
  readonly gateAngle: Float32Array;
  readonly gateCenterAngle: Float32Array;
  readonly gateWidth: Float32Array;
  readonly damage: Int32Array;
  readonly baseScore: Int32Array;
  /** RGBA, stride 4. */
  readonly dangerColor: Float32Array;
  /** RGBA, stride 4. */
  readonly gateColor: Float32Array;
  /** RGBA, stride 4. */
  readonly paintColor: Float32Array;
}

/** Scheduling hints, clamped exactly as Unity's `ChartDocument.ApplyTo` does. */
export interface CompiledChartRuntimeHints {
  readonly poolCapacity: number;
  readonly positionRerollLimit: number;
  readonly minimumDistanceFromPreviousOrigin: number;
  readonly randomSeed: number;
}

export interface CompiledChartMeta {
  readonly eventCount: number;
  /** True when `events` is already sorted by time, so `order` is the identity. */
  readonly isTimeAscending: boolean;
  /** False for charts written before `detectedBeatTimes` existed. */
  readonly hasDetectedBeatTimes: boolean;
  /** Earliest event time, or null for an empty chart. */
  readonly firstEventTime: number | null;
  /** Latest event time, or null for an empty chart. */
  readonly lastEventTime: number | null;
  /** Names of runtime hints whose value was clamped. Empty for well-formed charts. */
  readonly clampedHints: readonly string[];
}

export interface CompiledChart {
  readonly formatVersion: number;
  readonly difficulty: Difficulty;
  readonly bpm: number;
  readonly detectedBpm: number;
  readonly firstBeatOffsetSeconds: number;
  /** Detected beat grid in seconds, source order preserved. */
  readonly detectedBeatTimes: Float64Array;
  readonly events: CompiledWaveEvents;
  /**
   * Indices into `events`, ordered by ascending `time` and stable for ties.
   * Consume the chart through this rather than by sorting `events` in place.
   */
  readonly order: Uint32Array;
  readonly runtime: CompiledChartRuntimeHints;
  readonly meta: CompiledChartMeta;
}

export function compileChart(chartDocument: ChartDocumentV1): CompiledChart {
  const count = chartDocument.events.length;
  const events = allocate(count);

  for (let index = 0; index < count; index += 1) {
    const event = chartDocument.events[index];
    if (event === undefined) continue;
    events.time[index] = event.time;
    events.beat[index] = event.beat;
    events.initialRadius[index] = event.initialRadius;
    events.intensity[index] = event.intensity;
    events.spawnPositionMode[index] = event.spawnPositionMode;
    events.originX[index] = event.origin.x;
    events.originY[index] = event.origin.y;
    events.fixedWorldPositionX[index] = event.fixedWorldPosition.x;
    events.fixedWorldPositionY[index] = event.fixedWorldPosition.y;
    events.normalizedPositionX[index] = event.normalizedPosition.x;
    events.normalizedPositionY[index] = event.normalizedPosition.y;
    events.edgePadding[index] = event.edgePadding;
    events.minimumDistanceFromPlayer[index] = event.minimumDistanceFromPlayer;
    events.randomSeedOffset[index] = event.randomSeedOffset;
    events.warningLeadTime[index] = event.warningLeadTime;
    events.speed[index] = event.speed;
    events.gateAngle[index] = event.gateAngle;
    events.gateCenterAngle[index] = event.gateCenterAngle;
    events.gateWidth[index] = event.gateWidth;
    events.damage[index] = event.damage;
    events.baseScore[index] = event.baseScore;
    writeColor(events.dangerColor, index, event.dangerColor);
    writeColor(events.gateColor, index, event.gateColor);
    writeColor(events.paintColor, index, event.paintColor);
  }

  const isTimeAscending = checkAscending(events.time, count);
  const order = buildOrder(events.time, count, isTimeAscending);

  const clampedHints: string[] = [];
  const poolCapacity = clampMin(chartDocument.poolCapacity, 1, 'poolCapacity', clampedHints);
  const positionRerollLimit = clampMin(
    chartDocument.positionRerollLimit,
    1,
    'positionRerollLimit',
    clampedHints,
  );
  const minimumDistanceFromPreviousOrigin = clampMin(
    chartDocument.minimumDistanceFromPreviousOrigin,
    0,
    'minimumDistanceFromPreviousOrigin',
    clampedHints,
  );

  const firstIndex = order[0];
  const lastIndex = order[count - 1];

  return {
    formatVersion: chartDocument.formatVersion,
    difficulty: chartDocument.difficulty,
    bpm: chartDocument.bpm,
    detectedBpm: chartDocument.detectedBpm,
    firstBeatOffsetSeconds: chartDocument.firstBeatOffsetSeconds,
    detectedBeatTimes: Float64Array.from(chartDocument.detectedBeatTimes),
    events,
    order,
    runtime: {
      poolCapacity,
      positionRerollLimit,
      minimumDistanceFromPreviousOrigin,
      randomSeed: chartDocument.randomSeed,
    },
    meta: {
      eventCount: count,
      isTimeAscending,
      hasDetectedBeatTimes: chartDocument.detectedBeatTimes.length > 0,
      firstEventTime: firstIndex === undefined ? null : (events.time[firstIndex] ?? null),
      lastEventTime: lastIndex === undefined ? null : (events.time[lastIndex] ?? null),
      clampedHints,
    },
  };
}

/**
 * Rebuilds the plain event object at `index`.
 *
 * Intended for tests, tooling and diagnostics. The simulation reads the typed
 * arrays directly rather than materialising objects per frame.
 */
export function readCompiledEvent(chart: CompiledChart, index: number): WaveEventV1 {
  const events = chart.events;
  if (index < 0 || index >= events.count) {
    throw new RangeError(`Event index ${index} is out of range (0..${events.count - 1}).`);
  }
  return {
    time: at(events.time, index),
    beat: at(events.beat, index),
    initialRadius: at(events.initialRadius, index),
    intensity: at(events.intensity, index),
    spawnPositionMode: at(events.spawnPositionMode, index) as SpawnPositionMode,
    origin: vector(events.originX, events.originY, index),
    fixedWorldPosition: vector(events.fixedWorldPositionX, events.fixedWorldPositionY, index),
    normalizedPosition: vector(events.normalizedPositionX, events.normalizedPositionY, index),
    edgePadding: at(events.edgePadding, index),
    minimumDistanceFromPlayer: at(events.minimumDistanceFromPlayer, index),
    randomSeedOffset: at(events.randomSeedOffset, index),
    warningLeadTime: at(events.warningLeadTime, index),
    speed: at(events.speed, index),
    gateAngle: at(events.gateAngle, index),
    gateCenterAngle: at(events.gateCenterAngle, index),
    gateWidth: at(events.gateWidth, index),
    damage: at(events.damage, index),
    baseScore: at(events.baseScore, index),
    dangerColor: readColor(events.dangerColor, index),
    gateColor: readColor(events.gateColor, index),
    paintColor: readColor(events.paintColor, index),
  };
}

/** Reads an RGBA colour out of a stride-4 array. */
export function readColor(source: Float32Array, index: number): ColorV1 {
  const base = index * 4;
  return {
    r: source[base] ?? 0,
    g: source[base + 1] ?? 0,
    b: source[base + 2] ?? 0,
    a: source[base + 3] ?? 0,
  };
}

/**
 * Every typed array backing a `CompiledChart`, for `postMessage` transfer lists.
 * Order is unspecified and callers must not rely on it.
 */
export function collectTransferables(chart: CompiledChart): ArrayBuffer[] {
  const events = chart.events;
  const buffers = [
    chart.detectedBeatTimes.buffer,
    chart.order.buffer,
    events.time.buffer,
    events.beat.buffer,
    events.initialRadius.buffer,
    events.intensity.buffer,
    events.spawnPositionMode.buffer,
    events.originX.buffer,
    events.originY.buffer,
    events.fixedWorldPositionX.buffer,
    events.fixedWorldPositionY.buffer,
    events.normalizedPositionX.buffer,
    events.normalizedPositionY.buffer,
    events.edgePadding.buffer,
    events.minimumDistanceFromPlayer.buffer,
    events.randomSeedOffset.buffer,
    events.warningLeadTime.buffer,
    events.speed.buffer,
    events.gateAngle.buffer,
    events.gateCenterAngle.buffer,
    events.gateWidth.buffer,
    events.damage.buffer,
    events.baseScore.buffer,
    events.dangerColor.buffer,
    events.gateColor.buffer,
    events.paintColor.buffer,
  ];
  return buffers.filter((buffer): buffer is ArrayBuffer => buffer instanceof ArrayBuffer);
}

// ---------------------------------------------------------------------------

function allocate(count: number): CompiledWaveEvents {
  return {
    count,
    time: new Float64Array(count),
    beat: new Float64Array(count),
    initialRadius: new Float32Array(count),
    intensity: new Float32Array(count),
    spawnPositionMode: new Uint8Array(count),
    originX: new Float32Array(count),
    originY: new Float32Array(count),
    fixedWorldPositionX: new Float32Array(count),
    fixedWorldPositionY: new Float32Array(count),
    normalizedPositionX: new Float32Array(count),
    normalizedPositionY: new Float32Array(count),
    edgePadding: new Float32Array(count),
    minimumDistanceFromPlayer: new Float32Array(count),
    randomSeedOffset: new Int32Array(count),
    warningLeadTime: new Float32Array(count),
    speed: new Float32Array(count),
    gateAngle: new Float32Array(count),
    gateCenterAngle: new Float32Array(count),
    gateWidth: new Float32Array(count),
    damage: new Int32Array(count),
    baseScore: new Int32Array(count),
    dangerColor: new Float32Array(count * 4),
    gateColor: new Float32Array(count * 4),
    paintColor: new Float32Array(count * 4),
  };
}

function writeColor(target: Float32Array, index: number, color: ColorV1): void {
  const base = index * 4;
  target[base] = color.r;
  target[base + 1] = color.g;
  target[base + 2] = color.b;
  target[base + 3] = color.a;
}

function checkAscending(time: Float64Array, count: number): boolean {
  for (let index = 1; index < count; index += 1) {
    if ((time[index] ?? 0) < (time[index - 1] ?? 0)) return false;
  }
  return true;
}

/** Stable ascending-time permutation. Ties keep their relative source order. */
function buildOrder(time: Float64Array, count: number, isAscending: boolean): Uint32Array {
  const order = new Uint32Array(count);
  for (let index = 0; index < count; index += 1) order[index] = index;
  if (isAscending || count < 2) return order;

  const indices = Array.from(order);
  indices.sort((left, right) => {
    const difference = (time[left] ?? 0) - (time[right] ?? 0);
    return difference !== 0 ? difference : left - right;
  });
  order.set(indices);
  return order;
}

function clampMin(value: number, minimum: number, name: string, clamped: string[]): number {
  if (value < minimum) {
    clamped.push(name);
    return minimum;
  }
  return value;
}

function at(source: Float64Array | Float32Array | Int32Array | Uint8Array, index: number): number {
  return source[index] ?? 0;
}

function vector(x: Float32Array, y: Float32Array, index: number): Vector2V1 {
  return { x: x[index] ?? 0, y: y[index] ?? 0 };
}
